/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

import {
  ClinicIdSchema,
  EmailSchema,
  PasswordResetTokenSchema,
  PasswordSchema,
  TotpCodeSchema,
} from './primitives';

/**
 * Customer login — optional `clinicId` is the second step of the multi-clinic
 * picker flow: the first call returns the list of clinics for the email, then
 * the user confirms one and re-submits with `clinicId` set.
 */
export const CustomerLoginSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1, 'Password is required'),
  clinicId: ClinicIdSchema.optional(),
  totp: TotpCodeSchema.optional(),
  /** Keep me signed in — persistent refresh cookie vs session cookie. */
  rememberMe: z.boolean().optional(),
});
export type CustomerLoginInput = z.infer<typeof CustomerLoginSchema>;

/** Staff (Super Admin / Product Admin) login — TOTP required only when staff MFA is enabled. */
export const StaffLoginSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1, 'Password is required'),
  totp: TotpCodeSchema.optional(),
});
export type StaffLoginInput = z.infer<typeof StaffLoginSchema>;

/** Forgot-password request — generic; never reveals whether the email exists. */
export const ForgotPasswordSchema = z.object({
  email: EmailSchema,
});
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordSchema>;

/** Reset-password request — token is verified against its DB hash, not the
 *  plaintext, and is single-use. */
export const ResetPasswordSchema = z.object({
  token: PasswordResetTokenSchema,
  newPassword: PasswordSchema,
});
export type ResetPasswordInput = z.infer<typeof ResetPasswordSchema>;

/** Change-password request — used after sign-in to rotate the password. */
export const ChangePasswordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: PasswordSchema,
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password must differ from the current password',
    path: ['newPassword'],
  });
export type ChangePasswordInput = z.infer<typeof ChangePasswordSchema>;
